import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { BiMap, BiUpload, BiNote, BiDownload, BiRefresh, BiXCircle, BiCheckCircle, BiSolidFilePdf } from "react-icons/bi";
import { FaWalking, FaCar, FaBan, FaStar, FaImage, FaVideo, FaMicrophoneAlt, FaPlus } from "react-icons/fa";
import { FaLocationDot, FaHandshakeAngle } from "react-icons/fa6";

import { API_BASE_URL } from "../utils/urls";
// Assuming this form is now available
import FinalizeSurveillanceForm from './FinalizeSurveillanceForm';


const FieldUpdateSection = ({ caseId, caseName, subjects, canUpload }) => {
    // --- STATE CHANGES ---
    const [updates, setUpdates] = useState([]);
    const [surveillanceReports, setSurveillanceReports] = useState([]); // NEW STATE for reports
    const [note, setNote] = useState("");
    const [selectedSubjectId, setSelectedSubjectId] = useState("");
    const [files, setFiles] = useState([]);
    const [fieldworkStatus, setFieldworkStatus] = useState("in_progress");
    const [isAgentOrAdmin, setIsAgentOrAdmin] = useState(false);

    // GPS STATES
    const [location, setLocation] = useState({ lat: "", lng: "" });
    const [locationAddress, setLocationAddress] = useState('Location not captured.');
    const [isLocationFetching, setIsLocationFetching] = useState(false);
    const [selectedObservationType, setSelectedObservationType] = useState(null);
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [notes, setNotes] = useState("");

    const [mediaFiles, setMediaFiles] = useState([]);
    const photoInputRef = useRef(null);
    const videoInputRef = useRef(null);

    const phrasesByType = {
        vehicle: [
            "Subject entered vehicle",
            "Vehicle departed location",
            "Vehicle arrived at location",
            "Subject exited vehicle",
        ],
        movement: [
            "Subject walking northbound",
            "Subject changed direction",
            "Subject running",
            "Subject returned to location",
        ],
        location: [
            "Subject entered location",
            "Subject remained inside location",
            "Lights turned on",
            "Lights turned off",
        ],
        interaction: [
            "Subject met with unknown individual",
            "Brief conversation observed",
            "Items exchanged",
            "Group interaction observed",
        ],
        no_activity: [
            "No observable activity during this interval.",
        ],
        notable: [
            "Suspicious behavior observed",
            "Unusual delay",
            "Subject appeared aware of surveillance",
        ],
    };
    // ACTION STATES
    const [loading, setLoading] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [activeActionType, setActiveActionType] = useState(null);

    const user = JSON.parse(localStorage.getItem('user'));
    const roleName = user?.roleName || 'guest';
    const token = localStorage.getItem("token");

    const handlePhraseClick = (phrase) => {
        setNotes((prev) =>
            prev ? `${prev}\n${phrase}` : phrase
        );
    };

    const triggerFileInput = (type) => {
        if (type === "photo") photoInputRef.current.click();
        if (type === "video") videoInputRef.current.click();
    };


    const handleMediaSelect = async (event, type) => {
        const file = event.target.files[0];
        if (!file) return;

        const timestamp = new Date().toISOString();

        let gps = null;
        if (navigator.geolocation) {
            gps = await new Promise((resolve) => {
                navigator.geolocation.getCurrentPosition(
                    (pos) => {
                        resolve({
                            lat: pos.coords.latitude,
                            lng: pos.coords.longitude,
                            accuracy: pos.coords.accuracy, // Useful for debugging!
                        });
                    },
                    (error) => {
                        console.error("GPS Error:", error);
                        resolve(null);
                    },
                    {
                        enableHighAccuracy: true, // This is the most important fix
                        timeout: 10000,           // Wait up to 10 seconds for a lock
                        maximumAge: 0             // Don't use a cached location
                    }
                );
            });
        }

        const enrichedFile = {
            file,
            type,
            timestamp,
            gps,
        };

        setMediaFiles((prev) => [...prev, enrichedFile]);
    };


    const startVoiceRecording = async () => {
        try {
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert("Audio recording not supported in this browser.");
                return;
            }

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

            const mediaRecorder = new MediaRecorder(stream);
            const chunks = [];

            mediaRecorder.ondataavailable = (e) => chunks.push(e.data);

            mediaRecorder.onstop = () => {
                const blob = new Blob(chunks, { type: "audio/webm" });

                const enrichedFile = {
                    file: blob,
                    type: "voice",
                    timestamp: new Date().toISOString(),
                    gps: null,
                };

                setMediaFiles((prev) => [...prev, enrichedFile]);
                stream.getTracks().forEach((track) => track.stop());
            };

            mediaRecorder.start();

            setTimeout(() => {
                mediaRecorder.stop();
            }, 5000);

        } catch (err) {
            console.error("Microphone permission denied:", err);
            alert("Microphone permission denied. Please allow microphone access.");
        }
    };

    // --- Data Fetching ---
    const fetchUpdates = useCallback(async () => {
        try {
            // Backend now returns { updates: [...], surveillance_reports: [...] }
            const res = await axios.get(`${API_BASE_URL}/field-agent/field-updates/${caseId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });


            // Set both the updates and the reports
            setUpdates(res.data.updates || []);
            setSurveillanceReports(res.data.surveillance_reports || []);
            console.log("Case Data", res.data)

            // Check completion status based on updates
            const fetchedUpdates = res.data.updates || [];
            if (fetchedUpdates.length > 0 && fetchedUpdates.every((u) => u.status === "completed")) {
                setFieldworkStatus("completed");
            } else {
                setFieldworkStatus("in_progress");
            }
        } catch (err) {
            console.error("Error loading field updates:", err);
        }
    }, [caseId, token]);

    useEffect(() => {
        fetchUpdates();
        setIsAgentOrAdmin(roleName === 'field_agent' || roleName === 'admin' || roleName === 'case_manager');
    }, [caseId, fetchUpdates, roleName]);

    // --- Geolocation Logic (Unchanged) ---

    const getLocation = async () => {
        if (navigator.geolocation) {
            try {
                const gps = await new Promise((resolve, reject) => {
                    navigator.geolocation.getCurrentPosition(
                        (pos) =>
                            resolve({
                                lat: pos.coords.latitude,
                                lng: pos.coords.longitude,
                            }),
                        (err) => reject(err),
                        { enableHighAccuracy: true }
                    );
                });
            } catch (err) {
                console.warn("Location permission denied");
                alert("Location permission denied. Please enable location.");
                return;
            }
        }
        setIsLocationFetching(true);
        setLocationAddress('Attempting to get coordinates...');
        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            },
            () => {
                alert("Unable to access location. Please enable location permissions.");
                setIsLocationFetching(false);
                setLocationAddress('Failed to capture coordinates.');
            }
        );
    };

    const clearLocation = () => {
        setLocation({ lat: "", lng: "" });
        setLocationAddress('Location not captured.');
        setIsLocationFetching(false);
    };

    useEffect(() => {
        if (location.lat && location.lng) {
            setLocationAddress('Reverse geocoding coordinates...');
            axios.get(`${API_BASE_URL}/location?lat=${location.lat}&lon=${location.lng}`, {
                headers: { Authorization: `Bearer ${token}` }
            })
                .then(response => {
                    setLocationAddress(response.data?.location || 'Could not find address for these coordinates.');
                })
                .catch(error => {
                    console.error("API Call Error:", error);
                    setLocationAddress('Error fetching address from server.');
                })
                .finally(() => {
                    setIsLocationFetching(false);
                });
        }
    }, [location.lat, location.lng, token]);

    useEffect(() => {
        // Automatically get location when component mounts
        getLocation();
    }, []);

    // --- Submission Handlers (Unchanged) ---

    // 🔹 1. Submit a regular update
    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitAttempted(true);

        // FIX: Check mediaFiles length instead of the old files state
        if (!notes.trim() && mediaFiles.length === 0 && selectedObservationType !== 'no_activity') {
            alert("Please add a note or attach media.");
            return;
        }

        if (!location.lat || !location.lng) {
            alert("Please capture your location before submitting.");
            return;
        }

        const formData = new FormData();
        formData.append("case_id", caseId);
        if (selectedSubjectId) formData.append("subject_id", selectedSubjectId);
        formData.append("latitude", location.lat);
        formData.append("longitude", location.lng);
        formData.append("address", locationAddress);

        // Logic for "No Activity" vs Manual Notes
        const finalNote = selectedObservationType === "no_activity"
            ? "No observable activity during this interval."
            : notes;
        formData.append("notes", finalNote);

        // FIX: Append files from the mediaFiles array
        mediaFiles.forEach((item, index) => {
            // We use the 'file' property from your enrichedFile object
            // If it's a Voice Note (Blob), we give it a filename
            if (item.type === "voice") {
                formData.append("files", item.file, `voice_note_${Date.now()}.webm`);
            } else {
                formData.append("files", item.file);
            }
        });

        try {
            setLoading(true);
            await axios.post(`${API_BASE_URL}/field-agent/field-updates`, formData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data"
                },
            });

            // Reset all states
            setNotes("");
            setMediaFiles([]);
            setSelectedObservationType(null);
            setSelectedSubjectId("");
            setSubmitAttempted(false);
            fetchUpdates();
            alert("Field update saved successfully.");
        } catch (err) {
            console.error("Error submitting update:", err);
            alert(`Failed to save field update: ${err.response?.data?.message || err.message}`);
        } finally {
            setLoading(false);
        }
    };

    // 🔹 2. Handle Final Mark Complete (Surveillance Report Generation)
    const handleFinalMarkComplete = async (signatureFile) => {
        setCompleting(true);
        const subject = subjects.find(s => s.id.toString() === selectedSubjectId);
        const subjectName = subject ? subject.full_name : 'Case-level';

        const formData = new FormData();
        formData.append("case_id", caseId);
        if (selectedSubjectId) formData.append("subject_id", selectedSubjectId);
        formData.append("latitude", location.lat);
        formData.append("longitude", location.lng);
        formData.append("address", locationAddress);
        formData.append("signature_file", signatureFile);

        try {
            // Note: Updated the endpoint path to match the last provided route structure
            const res = await axios.post(
                `${API_BASE_URL}/field-agent/field-updates/surveillance-report/generate`,
                formData,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } }
            );

            setActiveActionType(null);
            clearLocation();
            await fetchUpdates();
            alert(res.data.message || `Surveillance Report for ${subjectName} generated and fieldwork marked complete.`);

        } catch (err) {
            console.error("Error generating report:", err);
            alert(`Failed to complete fieldwork: ${err.response?.data?.message || err.message}`);
        } finally {
            setCompleting(false);
        }
    };

    const selectedSubject = subjects.find(s => s.id.toString() === selectedSubjectId);

    // --- Report Helper ---
    const getSubjectName = (subjectId) => {
        const subject = subjects.find(s => s.id.toString() === subjectId);
        return subject ? subject.full_name : 'Case-Level';
    };

    // --- Render Logic ---
    if (!isAgentOrAdmin) return null;

    return (
        <div className="bg-white border border-gray-200 rounded-lg shadow-md mt-10 p-6">
            {/* --- Surveillance Reports Section (NEW) --- */}
            <div className="mb-6">
                <h4 className="font-bold text-lg text-indigo-700 flex items-center mb-2">
                    <BiSolidFilePdf className="w-5 h-5 mr-1" /> Final Surveillance Reports
                </h4>
                {surveillanceReports.length > 0 ? (
                    <ul className="space-y-2">
                        {surveillanceReports.map(report => (
                            <li key={report.id} className="p-3 border border-indigo-200 bg-indigo-50 rounded-md flex justify-between items-center">
                                <span className="text-sm font-medium text-indigo-800">
                                    Report: {report.file_name} (For {getSubjectName(report.subject_id)})
                                </span>
                                <a
                                    href={`${API_BASE_URL}${report.download_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs bg-indigo-600 text-white px-3 py-1 rounded-full hover:bg-indigo-700 flex items-center space-x-1"
                                >
                                    <BiDownload className="w-4 h-4" />
                                    <span>Download PDF</span>
                                </a>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-500 italic">No final surveillance reports generated yet.</p>
                )}
            </div>
            <h3 className="text-xl font-extrabold text-gray-900 mb-4 flex items-center space-x-2">
                <BiNote className="w-5 h-5" />
                <span>Field Updates & Surveillance Management</span>
            </h3>


            <hr className="my-6" />

            {/* 🟢 Status Indicator */}
            <div className="mb-4 text-sm font-medium">
                <div className="flex items-center space-x-2">
                    <span className="text-gray-700 text-xl">Case:</span>
                    <span className="font-semibold text-xl">{caseName}</span>

                    <span
                        className={`px-2 py-1 rounded ${fieldworkStatus === "completed"
                            ? "bg-green-100 text-green-700"
                            : "bg-yellow-100 text-yellow-700"
                            }`}
                    >
                        {fieldworkStatus === "completed" ? "Fieldwork Completed" : "Active"}
                    </span>
                </div>
            </div>

            {/* --- Location Capture Section (Unchanged) --- */}
            <div className="p-4 border border-blue-200 rounded-lg bg-blue-50 mb-6">
                <div className="flex items-center space-x-4 flex-wrap">

                    <div className="text-sm">
                        <p className="font-medium">Coordinates: {location.lat ? `${parseFloat(location.lat).toFixed(5)}, ${parseFloat(location.lng).toFixed(5)}` : 'Not captured'}</p>
                        <p className="text-gray-600">Address: <span className="font-semibold text-blue-700">{locationAddress}</span></p>
                    </div>
                </div>
            </div>

            {/* Render Finalize Form if active */}
            {activeActionType === 'complete' && (
                <FinalizeSurveillanceForm
                    selectedSubject={selectedSubject}
                    location={location}
                    onSubmit={handleFinalMarkComplete}
                    onCancel={() => setActiveActionType(null)}
                    isLoading={completing}
                />
            )}


            {/* 🧾 Upload / Update Form */}
            {activeActionType !== 'complete' && (
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="hidden">
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Update For
                        </label>
                        <select
                            value={selectedSubjectId}
                            onChange={(e) => setSelectedSubjectId(e.target.value)}
                            className="w-full border border-gray-300 rounded-md p-2 text-sm"
                        >
                            <option value="">Entire Case</option>
                            {subjects.map((s) => (
                                <option key={s.id} value={s.id}>
                                    Subject: {s.full_name || `Unnamed (ID: ${s.id})`}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Select Observation Type <span className="text-red-500">*</span>
                        </label>

                        <div className="grid grid-cols-3 gap-3">
                            {[
                                { value: "movement", label: "Movement", icon: <FaWalking /> },
                                { value: "vehicle", label: "Vehicle", icon: <FaCar /> },
                                { value: "location", label: "Location", icon: <FaLocationDot /> },
                                { value: "interaction", label: "Interaction", icon: <FaHandshakeAngle /> },
                                { value: "no_activity", label: "No Activity", icon: <FaBan /> },
                                { value: "notable", label: "Notable", icon: <FaStar /> },
                            ].map((type) => (
                                <button
                                    type="button"
                                    key={type.value}
                                    onClick={() => setSelectedObservationType(type.value)}
                                    className={`p-4 rounded-lg border text-sm font-medium transition-all
                            ${selectedObservationType === type.value
                                            ? "bg-blue-600 text-white border-blue-600 shadow-md"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                                        }`}
                                >
                                    <div className="flex items-center justify-center">
                                        {type.icon}
                                        <span className="ml-2">{type.label}</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Validation message example */}
                        {!selectedObservationType && submitAttempted && (
                            <p className="text-red-500 text-xs mt-2">
                                Observation type is required.
                            </p>
                        )}
                    </div>

                    {selectedObservationType == "no_activity" && (
                        <div className="mt-5">
                            <label className="block text-sm font-semibold text-gray-400 mb-2">
                                No observable activity during this interval.
                            </label>
                        </div>

                    )}

                    {selectedObservationType !== "no_activity" && (
                        <>
                            <div className="mt-5">
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Tap a phrase (optional)
                                </label>

                                <div className="flex gap-2 overflow-x-auto pb-2">
                                    {(phrasesByType[selectedObservationType] || []).map((phrase, index) => (
                                        <button
                                            type="button"
                                            key={index}
                                            onClick={() => handlePhraseClick(phrase)}
                                            className="whitespace-nowrap px-4 py-2 rounded-full text-sm border border-gray-300 bg-gray-100 hover:bg-blue-100 hover:border-blue-400 transition"
                                        >
                                            {phrase}
                                        </button>
                                    ))}
                                </div>
                            </div>


                            <div className="mt-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">
                                    Additional Details (Optional)
                                </label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    rows={4}
                                    className="w-full border border-gray-300 rounded-md p-3 text-sm"
                                    placeholder="Write observation notes..."
                                />
                            </div>


                            <div className="mt-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-3">
                                    4️⃣ Attach Media <span className="text-gray-400 text-xs">(Optional)</span>
                                </label>

                                <div className="grid grid-cols-3 gap-3">
                                    <button
                                        type="button"
                                        onClick={() => triggerFileInput("photo")}
                                        className="p-4 rounded-lg border bg-white text-sm font-medium border-gray-300 hover:bg-gray-100 transition"
                                    >
                                        <div className="flex items-center justify-center">

                                            <FaImage className="w-5 h-5" />
                                            <span className="ml-2">Photo</span>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => triggerFileInput("video")}
                                        className="p-4 rounded-lg border bg-white text-sm font-medium border-gray-300 hover:bg-gray-100 transition"
                                    >
                                        <div className="flex items-center justify-center">

                                            <FaVideo className="w-5 h-5" />
                                            <span className="ml-2">Video</span>
                                        </div>
                                    </button>

                                    <button
                                        type="button"
                                        onClick={startVoiceRecording}
                                        className="p-4 rounded-lg border bg-white text-sm font-medium border-gray-300 hover:bg-gray-100 transition"
                                    >
                                        <div className="flex items-center justify-center">
                                            <FaMicrophoneAlt className="w-5 h-5" />
                                            <span className="ml-2">Voice Note</span>
                                        </div>
                                    </button>
                                </div>

                                {/* Hidden File Inputs */}
                                <input
                                    type="file"
                                    accept="image/*"
                                    ref={photoInputRef}
                                    className="hidden"
                                    onChange={(e) => handleMediaSelect(e, "photo")}
                                />

                                <input
                                    type="file"
                                    accept="video/*"
                                    ref={videoInputRef}
                                    className="hidden"
                                    onChange={(e) => handleMediaSelect(e, "video")}
                                />

                                {mediaFiles.length > 0 && (
                                    <div className="mt-3 text-xs text-green-600">
                                        {mediaFiles.length} media file(s) attached
                                    </div>
                                )}
                            </div>
                        </>
                    )}


                    <div className="flex space-x-3 pt-2">
                        <button
                            type="submit"
                            disabled={loading}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-md text-sm font-semibold flex items-center space-x-1 disabled:opacity-50"
                        >
                            <FaPlus className="w-3 h-3" />
                            <span>{loading ? "Saving Update..." : "Log Entry"}</span>
                        </button>

                        <button
                            type="button"
                            disabled={completing}
                            onClick={() => setActiveActionType('complete')}
                            className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-md text-sm font-semibold flex items-center space-x-1 disabled:opacity-50"
                        >
                            <BiCheckCircle className="w-4 h-4" />
                            <span>End Surveillance</span>
                        </button>
                    </div>
                </form>
            )}

            {/* 🕓 List of Updates */}
            <div className="mt-6 border-t pt-6">
                <h4 className="font-semibold text-lg text-gray-800 mb-3">
                    Previous Updates
                </h4>
                {updates.length > 0 ? (
                    <ul className="space-y-3">
                        {updates.map((u) => (
                            <li
                                key={u.id}
                                className="p-3 bg-gray-50 border border-gray-200 rounded-md"
                            >
                                <p className="text-sm text-gray-800 font-medium">
                                    <b>From {u.agent_name || "Agent"}: </b>
                                    {u.notes}
                                </p>

                                {u.attachments && u.attachments.map((attachment, index) => (
                                    <a
                                        key={index}
                                        // The backend provided a file_path which needs to be part of the download query
                                        href={`${API_BASE_URL}/files/download?path=${encodeURIComponent(attachment.file_path)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs text-blue-600 hover:text-blue-900 flex items-center mt-1 space-x-1"
                                    >
                                        <BiDownload className="w-3 h-3" />
                                        <span>Download {attachment.file_type}: {attachment.file_name}</span>
                                    </a>
                                ))}

                                <p className="text-xs text-gray-500 mt-1">
                                    {u.address ? `📍Location: ${u.address}` : ''}
                                    {u.latitude && u.address ? ' • ' : ''}
                                    {u.latitude && u.longitude
                                        ? `Coords: ${parseFloat(u.latitude).toFixed(5)}, ${parseFloat(u.longitude).toFixed(5)}`
                                        : ""}
                                    {" • "}
                                    {new Date(u.created_at).toLocaleString()}
                                    {" • "}
                                    Status:{" "}
                                    <span
                                        className={`font-semibold ${u.status === "completed" ? "text-green-600" : "text-yellow-700"
                                            }`}
                                    >
                                        {u.status}
                                    </span>
                                </p>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="text-sm text-gray-500 italic">
                        No field updates recorded yet.
                    </p>
                )}
            </div>
        </div>
    );
};

export default FieldUpdateSection;